import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { css } from '@emotion/css';
import classNames from 'classnames';
import { find, get, rearg } from 'lodash';

import { Device, DrainContext, SortValue } from 'contexts/DrainContext';
import { Maybe } from 'models/types';
import {
  cardToProductWithIndex as Segment_cardToProductWithIndex,
  productListViewed as Segment_productListViewed,
  productsSearched as Segment_productsSearched,
} from 'bindings/Segment.bs';

import { FilterTaxonomy } from 'api/queries/filter';
import SelectLabel from './SelectLabel';
import SelectList from './SelectList';
import { getCardLists as CardSearchResults_getCardLists } from '../../../library/CardSearchResults.bs';

const classes = {
  dropdown: css`
    position: relative;
    //width: 160px;

    @media (min-width: 1288px) {
      //width: 200px;
    }
  `,
};

export type SortOption = {
  label: string;
  value: SortValue; // @NOTE: the type should be define and can be extended
};

const DEFAULT_SORT_OPTION_LIST: (filterList: FilterTaxonomy) => SortOption[] = (filterList: FilterTaxonomy) => {
  return [
    {
      label: get(filterList, 'sort.options.literalUp.title', 'A - Z'),
      value: SortValue.LiteralUp,
    },
    {
      label: get(filterList, 'sort.options.literalDown.title', 'Z - A'),
      value: SortValue.LiteralDown,
    },
    {
      label: get(filterList, 'sort.options.featured.title', 'Featured'),
      value: SortValue.Featured,
    },
    {
      label: get(filterList, 'sort.options.newArrivals.title', 'New Arrivals'),
      value: SortValue.NewArrivals,
    },
  ];
};

const DEFAULT_SORT_LABEL = 'Sort by';

type Props = {
  hidden: boolean;
  className?: Maybe<string>;
  filterList?: FilterTaxonomy;
};

const SelectSorting: React.FC<Props> = ({ className, filterList, hidden }) => {
  const drain = useContext(DrainContext);
  const router = useRouter();

  const sortLabel = find(DEFAULT_SORT_OPTION_LIST(filterList), ({ value }) => value === drain.sort).label;

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [activeOption, setActiveOption] = useState<SortOption>({
    value: drain.sort,
    label: drain.sort === SortValue.Featured ? DEFAULT_SORT_LABEL : sortLabel,
  });

  useEffect(() => {
    router.events.on('routeChangeStart', () => {
      drain.setSortValue(SortValue.Featured, SortValue.Featured);
    });
  }, []);

  const onClose = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const setSortOption = useCallback(
    (option: SortOption) => {
      drain.setSortValue(option.value, activeOption.value);
      drain.switchFilter({
        sorting: {
          current: option.value,
          previous: activeOption.value,
        },
        apply: true,
        display: false,
      });
      const products = drain.currentValue.current
        .filter(({ isSwappable }) => !isSwappable)
        .map(rearg(Segment_cardToProductWithIndex, [1, 0]));
      Segment_productListViewed({
        list_id: 'catalogue-promo',
        category: 'Featured',
        products,
      });
      setActiveOption(option);
    },
    [activeOption, drain.filter, drain.search]
  );

  return (
    <div className={classNames([classes.dropdown, className, hidden ? 'hidden' : void 0])}>
      <SelectLabel activeOption={activeOption} isOpen={isDropdownOpen} setOpen={setDropdownOpen} />
      <SelectList
        options={DEFAULT_SORT_OPTION_LIST(filterList)}
        activeOption={activeOption || DEFAULT_SORT_OPTION_LIST(filterList)[2]} // @note: the array key default option
        isDropdownOpen={isDropdownOpen}
        setSortOption={setSortOption}
        onClose={onClose}
      />
    </div>
  );
};

export default SelectSorting;
